import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth/useAuth";
import { env } from "../lib/env";
import {
  feedbackTriggerVisible,
  registerFeedbackPanel,
  setFeedbackTrigger,
  type NesitFeedbackPanel,
} from "../lib/feedback";

interface NesitFeedbackInitOptions {
  feedbackUrl: string;
  appId: string;
  branch?: string;
  commitSha?: string;
  userName?: string;
  userEmail?: string;
  userId?: string;
  locale?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  pageContext?: Record<string, string>;
  showTrigger?: boolean;
}

declare global {
  interface Window {
    NesitFeedback?: {
      init: (opts: NesitFeedbackInitOptions) => NesitFeedbackPanel;
      update?: (opts: Partial<NesitFeedbackInitOptions>) => void;
      destroy?: () => void;
    };
  }
}

const SCRIPT_ID = "senacle-feedback-widget-script";
const SCRIPT_SRC = "/feedback-widget.js";

function loadScript(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    if (window.NesitFeedback) return Promise.resolve();
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("feedback-widget script load failed")),
        { once: true },
      );
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("feedback-widget script load failed"));
    document.head.appendChild(s);
  });
}

// Une instance pour toute l'app : MobileShell et DesktopShell montent chacun ce
// composant, et franchir 900 px démonte l'un pour monter l'autre. Un ref par
// composant relançait init (second bouton flottant, second enregistreur rrweb).
let initialised = false;

// showTrigger vient du shell : false sur mobile, où le bouton flottant
// recouvrirait la barre d'onglets et où l'entrée passe par le Profil.
export function FeedbackWidget({ showTrigger }: { showTrigger: boolean }) {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    setFeedbackTrigger(showTrigger);
  }, [showTrigger]);

  useEffect(() => {
    if (!env.VITE_FEEDBACK_ENABLED) return;
    if (initialised) return;
    let cancelled = false;

    void loadScript()
      .then(() => {
        if (cancelled || initialised) return;
        if (!window.NesitFeedback) {
          if (import.meta.env.DEV) {
            console.warn("[feedback-widget] global not exposed after load");
          }
          return;
        }
        const profile = user?.profile as
          | {
              name?: string;
              email?: string;
              preferred_username?: string;
              sub?: string;
            }
          | undefined;
        const panel = window.NesitFeedback.init({
          feedbackUrl: env.VITE_FEEDBACK_URL,
          appId: env.VITE_FEEDBACK_APP_ID,
          branch: env.VITE_FEEDBACK_BRANCH,
          commitSha: env.VITE_FEEDBACK_COMMIT,
          userName: profile?.name ?? profile?.preferred_username,
          userEmail: profile?.email,
          userId: profile?.sub,
          locale: "fr",
          position: "bottom-right",
          pageContext: { route: location.pathname },
          showTrigger: feedbackTriggerVisible(),
        });
        registerFeedbackPanel(panel);
        initialised = true;
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.warn("[feedback-widget] failed to load", err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!initialised) return;
    if (!window.NesitFeedback?.update) return;
    window.NesitFeedback.update({
      pageContext: { route: location.pathname },
    });
  }, [location.pathname]);

  return null;
}
